import Head from 'next/head';
import { useRouter } from 'next/router';

import type { BreadcrumbType } from '@/types/BreadcrumbType';
import { AppConfig } from '@/utils/AppConfig';

type IMetaProps = {
  title?: string;
  titleTemplate?: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogShareImg?: string;
  jsonLdType?: string;
  jsonLdBreadcrumb?: BreadcrumbType[];
  jsonLdPricing?: string;
  jsonLdAggregateRatingValue?: string;
  jsonLdAggregateRatingCount?: string;
  hideJsonLdSchema?: string;
  noFollowContent?: string;
  hideAggregateRating?: string;
};

const Meta = (props: IMetaProps) => {
  const router = useRouter();

  let breadcrumbArrString = '';
  if (
    props.jsonLdBreadcrumb !== undefined &&
    props.jsonLdBreadcrumb.length > 0
  ) {
    props.jsonLdBreadcrumb.forEach((breadcrumb: BreadcrumbType) => {
      breadcrumbArrString += `{"@type":"ListItem","position":${breadcrumb.position},"name":"${breadcrumb.name}"`;
      breadcrumbArrString +=
        breadcrumb.item !== '' ? `,"item":"${breadcrumb.item}"},` : '},';
    });
    breadcrumbArrString = breadcrumbArrString.slice(0, -1);
  }
  const breadcrumbJsonLd =
    breadcrumbArrString !== ''
      ? `{"@type":"BreadcrumbList","@id":"${
          process.env.NEXT_PUBLIC_HOMESITE_URL! + props.canonical
        }#breadcrumb","itemListElement":[${breadcrumbArrString}]}`
      : '';

  let startsFromPrice = '9.99';
  if (props.jsonLdPricing !== undefined && props.jsonLdPricing !== '')
    startsFromPrice = props.jsonLdPricing;

  const pricingJsonLd = `"offers":{"@type":"Offer","price":"${startsFromPrice}","priceCurrency":"USD"}`;

  const contactJsonLd = `"contactPoint":{"@type":"ContactPoint","availableLanguage":["English"],"contactType":"Customer service","email":"support@taxzerone.com"},"duns":"118828993","email":"support@taxzerone.com"`;
  const socialmediaJsonLd = `"sameAs":["https://www.facebook.com/taxzerone","https://twitter.com/TaxZeroneUS","https://www.instagram.com/taxzerone/","https://www.linkedin.com/company/taxzerone","https://www.youtube.com/@taxzerone","https://www.pinterest.com/taxzerone/"]`;
  const logoJsonLd = `"logo":{"@type":"ImageObject","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#logo","caption":"${AppConfig.site_name}","contentUrl":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }assets/images/logo.png","height":160,"inLanguage":"en-US","url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }assets/images/logo.png","width":600}`;
  const aggregateRatingJsonLd =
    props.hideAggregateRating === undefined
      ? `"aggregateRating":{"@type":"AggregateRating","ratingValue":"${
          props.jsonLdAggregateRatingValue !== undefined &&
          props.jsonLdAggregateRatingValue !== ''
            ? props.jsonLdAggregateRatingValue
            : '4.95'
        }","ratingCount":"${
          props.jsonLdAggregateRatingCount !== undefined &&
          props.jsonLdAggregateRatingCount !== ''
            ? props.jsonLdAggregateRatingCount
            : '4047'
        }"}`
      : '';

  // don't remove the $$BREADCRUMB in the below line
  let websiteJsonLd = `{"@context":"https://schema.org","@graph":[{"@type":"Organization","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#organization",${contactJsonLd},"keywords":"${
    AppConfig.site_keywords
  }",${logoJsonLd},"name":"${AppConfig.org_name}",${socialmediaJsonLd},"url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }"},{"@type":"SoftwareApplication","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#SoftwareApplication","applicationCategory":"https://schema.org/WebApplication",${
    aggregateRatingJsonLd !== '' ? `${aggregateRatingJsonLd},` : ''
  }"countriesSupported":"US","inLanguage":"en-US","name":"${
    AppConfig.site_name
  }","operatingSystem":"Windows 7, OSX 10.6, Android 6.0 and up","publisher":{"@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#organization"},${pricingJsonLd},"url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }"}$$BREADCRUMB]}`;
  websiteJsonLd = websiteJsonLd.replace(
    '$$BREADCRUMB',
    breadcrumbJsonLd !== '' ? `,${breadcrumbJsonLd}` : ''
  );

  const webpageJsonLd = `{"@context":"https://schema.org","@graph":[{"@type":"Organization","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#organization","name":"${AppConfig.site_name}","url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }","sameAs":["https://www.facebook.com/taxzerone","https://twitter.com/TaxZeroneUS","https://www.instagram.com/taxzerone/","https://www.linkedin.com/company/taxzerone","https://www.youtube.com/@taxzerone","https://www.pinterest.com/taxzerone/"],"logo":{"@type":"ImageObject","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#logo","inLanguage":"en-US","url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }assets/images/logo.png","contentUrl":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }assets/images/logo.png","width":600,"height":160,"caption":"${
    AppConfig.site_name
  }"},"image":{"@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#logo"}},{"@type":"WebSite","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#website","url":"${process.env.NEXT_PUBLIC_HOMESITE_URL!}","name":"${
    AppConfig.site_name
  }","description":"${AppConfig.home_title}","publisher":{"@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#organization"},"inLanguage":"en-US"},{"@type":"WebPage","@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#webpage","url":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL! + props.canonical
  }","name":"${
    props.titleTemplate
      ? `${props.titleTemplate}|${AppConfig.title}`
      : props.title
  }","isPartOf":{"@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#website"},"about":{"@id":"${
    process.env.NEXT_PUBLIC_HOMESITE_URL!
  }#organization"},"description":"${
    props.description
  }","inLanguage":"en-US"}]}`;

  return (
    <>
      <Head>
        <title key="title">
          {props.titleTemplate
            ? `${props.titleTemplate} | ${AppConfig.title}`
            : props.title}
        </title>
        <meta charSet="UTF-8" key="charset" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
          key="viewport"
        />
        <meta name="description" content={props.description} />
        <link
          rel="canonical"
          href={process.env.NEXT_PUBLIC_HOMESITE_URL! + props.canonical}
        />
        <meta name="author" content={AppConfig.site_name} />
        {/* <meta name="keywords" content={props.keywords} /> */}
        <meta
          property="og:title"
          content={
            props.titleTemplate
              ? `${props.titleTemplate} | ${AppConfig.title}`
              : props.title
          }
        />
        <meta
          property="og:url"
          content={process.env.NEXT_PUBLIC_HOMESITE_URL! + props.canonical}
        />
        <meta
          property="og:image"
          content={
            props.ogShareImg
              ? `${process.env.NEXT_PUBLIC_HOMESITE_URL! + props.ogShareImg}`
              : `${
                  process.env.NEXT_PUBLIC_HOMESITE_URL!
                }assets/images/share-img.png`
          }
        />
        <meta
          property="og:image:secure_url"
          content={
            props.ogShareImg
              ? `${process.env.NEXT_PUBLIC_HOMESITE_URL! + props.ogShareImg}`
              : `${
                  process.env.NEXT_PUBLIC_HOMESITE_URL!
                }assets/images/share-img.png`
          }
        />

        <meta property="og:description" content={props.description} />
        <meta
          property="og:type"
          content={props.ogType ? props.ogType : 'website'}
        />
        <meta property="og:site_name" content={AppConfig.site_name} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content={
            props.ogShareImg
              ? `${process.env.NEXT_PUBLIC_HOMESITE_URL! + props.ogShareImg}`
              : `${
                  process.env.NEXT_PUBLIC_HOMESITE_URL!
                }assets/images/share-img.png`
          }
        />
        <meta
          name="twitter:title"
          content={
            props.titleTemplate
              ? `${props.titleTemplate} | ${AppConfig.title}`
              : props.title
          }
        />
        <meta name="twitter:description" content={props.description} />

        <meta name="twitter:site" content={AppConfig.site_name} />
        <meta name="twitter:handle" content="TaxZeroneUS" />
        {process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? (
          <meta
            name="robots"
            content={
              props.noFollowContent ||
              'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
            }
          />
        ) : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <meta name="theme-color" content="#213e99" />

        <link
          rel="apple-touch-icon"
          href={`${router.basePath}/apple-touch-icon.png`}
          key="apple"
        />
        <link
          rel="shortcut icon"
          type="image/png"
          sizes="32x32"
          href={`${router.basePath}/favicon-32x32.png`}
        />
        <link
          rel="image_src"
          type="image/png"
          sizes="32x32"
          href={`${router.basePath}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${router.basePath}/favicon-32x32.png`}
          key="icon32"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${router.basePath}/favicon-16x16.png`}
          key="icon16"
        />
        <link
          rel="icon"
          type="image/x-icon"
          href={`${router.basePath}/favicon.ico`}
          key="favicon"
        />
        <link
          rel="Shortcut icon"
          type="image/x-icon"
          href={`${router.basePath}/assets/images/favicon.ico`}
        />
        {((props.jsonLdType !== undefined && props.jsonLdType === 'Website') ||
          props.jsonLdType === undefined) &&
          props.hideJsonLdSchema === undefined && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: websiteJsonLd,
              }}
            />
          )}

        {props.jsonLdType !== undefined && props.jsonLdType === 'Webpage' && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: webpageJsonLd,
            }}
          />
        )}
      </Head>
    </>
  );
};

export { Meta };
